import React, { useState, useEffect, useRef } from "react";
import { db } from "./firebase";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import FlashcardItem from "./flashcards/FlashcardItem";
import ProgressBar from "./ProgressBar";
import GenerateIcon from "@mui/icons-material/AutoAwesome";
import ArrowCircleDownIcon from "@mui/icons-material/ArrowCircleDown";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import AddIcon from "@mui/icons-material/Add";
import { ThemeSelectorFlow } from "./ThemeSelectorFlow";
import { AIModelInfoTooltip } from "./AIModelInfoDialog";
import {
  trackLesson,
  BackButton,
  checkLessonLimit,
  escapeLatexInJson,
} from "./functions";

import { DashedPaper } from "../StyledComponents";
import { AIModelSelect } from "./TestComponents2";
import QuestionMarkIcon from "@mui/icons-material/Help";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import { AnimatedStep } from "./Animations";
import LoadingDialog from "./LoadingDialog";
import SaveAsIcon from "@mui/icons-material/SaveAs";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import {
  Alert,
  Snackbar,
  Collapse,
  Grid,
  Tooltip,
  Fade,
  FormControl,
  Select,
  MenuItem,
  Stack,
} from "@mui/material";
import { Link as LinkIcon } from "@mui/icons-material";
import axios from "axios";
import FlashcardImageUpload from "./flashcards/FlashcardImageUpload";
import SettingsIcon from "@mui/icons-material/Settings";

import InfoBox from "./InfoBox";

import LanguageSelector from "./LanguageSelector";
import ShareDialog from "./ShareDialog";
import Logger from "./Logger";
import {
  useParams,
  useNavigate,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import { East as ArrowIcon } from "@mui/icons-material";
import ShareIcon from "@mui/icons-material/Share";

import ViewAgendaIcon from "@mui/icons-material/ViewAgenda";

import AdditionalFlashFields from "./AdditionalFlashFields";
import { useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import { getStyles } from "../index";

import { callApi } from "./api";
import LoadingSpinner from "./LoadingSpinner";
import { useSnackbar } from "./SnackbarHandler";

import ExpandLessIcon from "@mui/icons-material/ExpandLess";

import LoadingBackdrop from "./LoadingBackdrop";
import { sanitizeInput } from "./functions";

import MyFlashcards from "./flashcards/MyFlashcards";

import { getThemes } from "./additionalThemes/Flashcards";

const FlashcardsCreateView = ({
  user,
  onCollectionCreated,
  isDark,
  startTraining,
  setMinimized,
}) => {
  const [collectionName, setCollectionName] = useState("");
  const [flashcards, setFlashcards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [focusIndex, setFocusIndex] = useState(null);

  const [showAIInput, setShowAIInput] = useState(true); // State to toggle AI input box
  const [aiInput, setAIInput] = useState(""); // State to store AI input
  const [inputType, setInputType] = useState("subject"); // State to manage which radio button is selected
  const [shareDialogOpenFriends, setShareDialogOpenFriends] = useState(false);
  const [searchParams] = useSearchParams();
  const [error, setError] = useState(null);
  const [isPublic, setIsPublic] = useState(true);
  const [viewIsLoading, setViewIsLoading] = useState(true);

  const bookContentRef = useRef("");
  const [myFlashcardsReloadTrigger, setMyFlashcardsReloadTrigger] = useState(0);

  const [selectedModel, setSelectedModel] = useState("Studera.AI");

  const selectedSuggestionRef = useRef("");
  const [progress, setProgress] = useState(0);

  const [isSharing, setIsSharing] = useState(false);

  const [visibility, setVisibility] = useState("public");
  const collectionNameRef = useRef(null);
  const theme = useTheme();
  const styles = getStyles(theme);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [showMoreOptions, setShowMoreOptions] = useState(false);
  const [numFlashcards, setNumFlashcards] = useState(10);
  const [category, setCategory] = useState("Välj kategori");
  const [language, setLanguage] = useState("svenska");
  const [difficulty, setDifficulty] = useState("Medel");
  const [aiInstructions, setAiInstructions] = useState("");

  const [loadingSaving, setLoadingSaving] = useState(false);

  const { showSnackbar } = useSnackbar();
  const urlContentRef = useRef("");
  const subjectInputRef = useRef(null);

  const [renderedFlashcards, setRenderedFlashcards] = useState([]);
  const [renderInterval, setRenderInterval] = useState(null);

  const location = useLocation();

  const [customTheme, setCustomTheme] = useState("");
  const [lessonTheme, setLessonTheme] = useState("");
  const [predefinedTheme, setPredefinedTheme] = useState(null);

  const transcriptRef = useRef("");
  const uploadedFileRef = useRef("");

  const wikipediaContentRef = useRef("");
  const randomizedContentRef = useRef("");
  const coursePlanContentRef = useRef("");

  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const subject = searchParams.get("subject") || null;
  const additionalThemes = getThemes(subject);

  const analysisContentRef = useRef("");
  const pasteTextContentRef = useRef("");

  const [aiFlashcardDialogOpen, setAiFlashcardDialogOpen] = useState(false);
  const [aiFlashcardPrompt, setAiFlashcardPrompt] = useState("");
  const [isGeneratingFlashcard, setIsGeneratingFlashcard] = useState(false);

  // Add these handler functions
  const handleOpenAiFlashcardDialog = () => {
    setAiFlashcardDialogOpen(true);
  };

  const handleCloseAiFlashcardDialog = () => {
    setAiFlashcardDialogOpen(false);
    setAiFlashcardPrompt("");
  };

  // Modify the handleAddFlashcardWithAI function to include a temporary loading state
  // Modify the handleAddFlashcardWithAI function with better instructions
  const handleAddFlashcardWithAI = async () => {
    // Close the dialog first
    handleCloseAiFlashcardDialog();

    // Create a temporary loading flashcard
    const loadingFlashcard = {
      front: "Genererar med AI...",
      back: "Vänligen vänta...",
      correct: "false",
      imageUrlFront: "",
      imageUrlBack: "",
      isGenerating: true, // Flag to identify this is a loading flashcard
    };

    // Add the temporary flashcard to the list
    const newFlashcardsWithLoading = [...flashcards, loadingFlashcard];
    setFlashcards(newFlashcardsWithLoading);

    // Scroll to the new flashcard
    setTimeout(() => {
      const flashcardItems = document.querySelectorAll(".flashcard-item");
      const lastFlashcard = flashcardItems[flashcardItems.length - 1];
      lastFlashcard?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);

    try {
      // Get a subset of existing flashcards for context (limit to 5 to avoid too much context)
      const flashcardsForContext =
        flashcards.length > 5
          ? flashcards.slice(-5) // Only the latest 5 flashcards if there are many
          : flashcards; // All flashcards if less than 5

      // Create messages for AI with better instructions
      // Fixed version with the specific return instruction
      const messages = [
        {
          role: "system",
          content: `Du är en expert på att skapa pedagogiska flashcards. Du ska skapa ett nytt flashcard i det angivna formatet baserat på instruktionerna. Svara ENDAST med ett giltigt JSON-objekt med "front" och "back" fält.


Om det är matematik-tema: När du använder LaTeX-syntax MÅSTE du omsluta VARJE matematiskt uttryck (oavsett om det är enkla operationer eller komplexa ekvationer) med dollartecken på följande sätt:

För uttryck inom textrader: $x \times y$
För fristående uttryck: $$x \times y$$

Använd ALDRIG [], () eller andra tecken runt matematiska uttryck. ALLTID dollartecken.



          `,
        },
        {
          role: "user",
          content: `Här är alla befintliga flashcards i samlingen:\n\n${JSON.stringify(flashcardsForContext, null, 2)}\n\nSkapa ett nytt flashcard i samma JSON-format som de befintliga flashcards. Följande instruktion beskriver vad flashcardet ska handla om: ${aiFlashcardPrompt ? aiFlashcardPrompt : "Lägg till ett flashcard som kompletterar de andra flashcards bra."}. \n\nFlashcardet ska vara på ${language} och bör INTE vara för likt något av de befintliga flashcards. Försök att täcka andra aspekter eller områden inom samma tema. Returnera endast det nya JSON-objektet med samma struktur som de befintliga flashcards.`,
        },
      ];

      // Call the API
      const response = await callApi(messages, 2000, true, user.uid);

      // Extract the response content
      const messageContent = response.data.choices[0].message.content.trim();

      // Parse the JSON from the response
      let parsedResponse;
      try {
        // Extract JSON if it's wrapped in code blocks
        let jsonContent = messageContent;
        if (jsonContent.startsWith("```json")) {
          jsonContent = jsonContent.slice(7, -3).trim();
        } else if (jsonContent.startsWith("```")) {
          jsonContent = jsonContent.slice(3, -3).trim();
        }
        jsonContent = escapeLatexInJson(jsonContent);

        parsedResponse = JSON.parse(jsonContent);

        // Create the real flashcard
        const newFlashcard = {
          front: parsedResponse.front,
          back: parsedResponse.back,
          correct: "false",
          imageUrlFront: "",
          imageUrlBack: "",
        };

        // Replace the loading flashcard with the real one
        const updatedFlashcards = flashcards.filter(
          (card) => !card.isGenerating,
        );
        setFlashcards([...updatedFlashcards, newFlashcard]);
        //setHasUnsavedChanges(true);

        // Track action
        await trackLesson(user.uid, "FlashcardViewNewCard.js", null, null);

        showSnackbar("Flashcard har skapats!", "success");
      } catch (parseError) {
        console.error("Error parsing JSON response:", parseError);
        showSnackbar(
          "Det gick inte att skapa flashcardet. Försök igen.",
          "error",
        );

        // Remove the loading flashcard if there was an error
        const updatedFlashcards = flashcards.filter(
          (card) => !card.isGenerating,
        );
        setFlashcards(updatedFlashcards);
      }
    } catch (error) {
      console.error("Error generating flashcard:", error);
      showSnackbar(
        "Det gick inte att skapa flashcardet. Försök igen.",
        "error",
      );

      // Remove the loading flashcard if there was an error
      const updatedFlashcards = flashcards.filter((card) => !card.isGenerating);
      setFlashcards(updatedFlashcards);
    }
  };

  const resetThemeContent = () => {
    setLessonTheme("");
    setCustomTheme("");
    setPredefinedTheme(null);
    transcriptRef.current = "";
    uploadedFileRef.current = "";
    wikipediaContentRef.current = "";
    randomizedContentRef.current = "";
    bookContentRef.current = "";
    urlContentRef.current = "";
    analysisContentRef.current = "";
    coursePlanContentRef.current = "";
    pasteTextContentRef.current = "";
  };
  // Add this effect to your component
  useEffect(() => {
    if (focusIndex !== null) {
      const element = document.getElementById(`front-${focusIndex}`);
      if (element) {
        element.focus();
      }
      setFocusIndex(null); // Reset after focusing
    }
  }, [focusIndex]);
  const [currentStep, setCurrentStep] = useState(1);
  useEffect(() => {
    if (currentStep === 2 && flashcards.length > 0) {
      setCurrentStep(3);
      setMinimized(true);
    }
  }, [flashcards, currentStep]);

  // useEffect(() => {
  //   if (flashcards.length > 1) {
  //     setTimeout(() => {
  //       const addMore = document.querySelector(".addMore");
  //       if (addMore) {
  //         addMore.scrollIntoView({
  //           behavior: "smooth",
  //           block: "end",
  //         });
  //       }
  //     }, 500);
  //   }
  // }, [flashcards]);

  useEffect(() => {
    return () => {
      if (renderInterval) {
        clearInterval(renderInterval);
      }
    };
  }, [renderInterval]);

  useEffect(() => {
    setViewIsLoading(false);

    // const checkLimit = async () => {
    //   const withinLimit = await checkLessonLimit(
    //     "QxYVwryA6TfJMUjZ6gP2wcLwxN02",
    //   );
    //   Logger.log("withinLimit: ", withinLimit);
    // };

    // // Call the async function
    // checkLimit();
  }, []);

  if (viewIsLoading) {
    return <LoadingSpinner />;
  }

  const handleShare = async ({ roomId, studentIds, comment, fileUrl }) => {
    setIsSharing(true);

    await new Promise((resolve) => setTimeout(resolve, 1500));
    // try {
    //   // Create a document for each student
    //   const sharePromises = studentIds.map((studentId) => {
    //     const documentData = {
    //       sharedTo: studentId,
    //       comment: comment,
    //       fileUrl: fileUrl,
    //       shared: true,
    //       userId: user.uid,
    //       collectionName: collectionName,
    //       flashcards: flashcards.map((card) => ({
    //         front: sanitizeInput(card.front),
    //         back: sanitizeInput(card.back),
    //         correct: card.correct,
    //         imageUrlFront: card.imageUrlFront || "",
    //         imageUrlBack: card.imageUrlBack || "",
    //       })),
    //       isPublic,
    //       createdAt: Date.now(),
    //       numFlashcards,
    //       category: category,
    //       language: language,
    //       difficulty: difficulty,
    //       aiInstructions: aiInstructions,
    //       selectedModel: selectedModel,
    //       lastTrainingSession: null,
    //       upvotes: 0,
    //       downvotes: 0,
    //       userVotes: {},
    //     };

    //     Logger.log(
    //       "Adding document for student",
    //       studentId,
    //       ":",
    //       JSON.stringify(documentData, null, 2),
    //     );

    //     return addDoc(collection(db, "flashcardCollections"), documentData);
    //   });

    //   await Promise.all(sharePromises);

    //   showSnackbar(`Du har nu delat flashcards med eleverna!`, "success");
    //   setCollectionName("");
    //   setFlashcards([
    //     {
    //       front: "",
    //       back: "",
    //       correct: "false",
    //       imageUrlFront: "",
    //       imageUrlBack: "",
    //     },
    //   ]);
    // }
    try {
      // Create a document for each student
      const sharePromises = studentIds.map(async (studentId) => {
        const documentData = {
          sharedTo: studentId,
          comment: comment,
          fileUrl: fileUrl,
          shared: true,
          userId: user.uid,
          collectionName: collectionName,
          flashcards: flashcards.map((card) => ({
            front: sanitizeInput(card.front),
            back: sanitizeInput(card.back),
            correct: card.correct,
            imageUrlFront: card.imageUrlFront || "",
            imageUrlBack: card.imageUrlBack || "",
          })),
          isPublic,
          createdAt: Date.now(),
          numFlashcards,
          category: category,
          language: language,
          difficulty: difficulty,
          aiInstructions: aiInstructions,
          selectedModel: selectedModel,
          lastTrainingSession: null,
          upvotes: 0,
          downvotes: 0,
          userVotes: {},
        };

        // Create the flashcard collection document and get its ID
        const flashcardDoc = await addDoc(
          collection(db, "flashcardCollections"),
          documentData,
        );
        Logger.log(
          "Adding document for student",
          studentId,
          ":",
          JSON.stringify(documentData, null, 2),
        );

        // Create notification with the flashcard collection doc ID
        await addDoc(collection(db, "notifications"), {
          type: "flashcards_shared",
          recipientId: studentId,
          senderId: user.uid,
          read: false,
          createdAt: serverTimestamp(),
          flashcardData: {
            collectionId: flashcardDoc.id,
            collectionName: documentData.collectionName,
            fileUrl: documentData.fileUrl,
            comment: documentData.comment,
            category: documentData.category,
            difficulty: documentData.difficulty,
            numFlashcards: documentData.numFlashcards,
          },
        });

        return flashcardDoc;
      });

      await Promise.all(sharePromises);

      await trackLesson(user.uid, "FlashcardsShared.js", null, null);

      showSnackbar(`Du har nu delat flashcards med eleverna!`, "success");
      // setCollectionName("");
      // setFlashcards([
      //   {
      //     front: "",
      //     back: "",
      //     correct: "false",
      //     imageUrlFront: "",
      //     imageUrlBack: "",
      //   },
      // ]);
    } catch (error) {
      showSnackbar("Fel vid delande av flashcards: " + error.message, "error");
    } finally {
      setIsSharing(false);
    }
  };

  const handleSaveFlashcards = async () => {
    if (!collectionName.trim()) {
      showSnackbar("Samlingens namn får inte vara tomt.", "info");
      collectionNameRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return;
    }
    if (
      flashcards.some((card) => !card?.front?.trim() || !card?.back?.trim())
    ) {
      showSnackbar(
        "Inga fält för framsida eller baksida får vara tomma.",
        "info",
      );
      return;
    }

    setLoadingSaving(true);
    let userId = user.uid;

    try {
      // Sanitize the collection name and flashcards
      const sanitizedCollectionName = sanitizeInput(collectionName);
      const sanitizedFlashcards = flashcards.map((card) => ({
        front: sanitizeInput(card.front),
        back: sanitizeInput(card.back),
        correct: card.correct,
        imageUrlFront: card.imageUrlFront || "",
        imageUrlBack: card.imageUrlBack || "",
      }));

      // Sanitize other fields
      const sanitizedCategory = sanitizeInput(category);
      const sanitizedLanguage = sanitizeInput(language);
      const sanitizedDifficulty = sanitizeInput(difficulty);
      const sanitizedAiInstructions = sanitizeInput(aiInstructions);
      const sanitizedSelectedModel = sanitizeInput(selectedModel);

      const collectionRef = await addDoc(
        collection(db, "flashcardCollections"),
        {
          userId,
          collectionName: sanitizedCollectionName,
          flashcards: sanitizedFlashcards,
          isPublic,
          createdAt: Date.now(),
          numFlashcards,
          category: sanitizedCategory,
          language: sanitizedLanguage,
          difficulty: sanitizedDifficulty,
          aiInstructions: sanitizedAiInstructions,
          selectedModel: sanitizedSelectedModel,
          lastTrainingSession: null,
          upvotes: 0,
          downvotes: 0,
          userVotes: {},
        },
      );

      showSnackbar(`Flashcards sparade!`, "success");
      // setCollectionName("");
      // setFlashcards([
      //   {
      //     front: "",
      //     back: "",
      //     correct: "false",
      //     imageUrlFront: "",
      //     imageUrlBack: "",
      //   },
      // ]);

      // setMyFlashcardsReloadTrigger((prev) => prev + 1);
    } catch (error) {
      showSnackbar("Fel vid sparande av samling: " + error.message, "error");
    } finally {
      setLoadingSaving(false);
    }
  };

  const handlePublicChange = (event) => {
    const newVisibility = event.target.checked ? "public" : "private";
    setVisibility(newVisibility);
    setIsPublic(newVisibility === "public");
  };

  const handleCollectionNameChange = (e) => {
    setCollectionName(e.target.value);
  };

  const handleFrontChange = (index, e) => {
    const newFlashcards = [...flashcards];
    newFlashcards[index].front = e.target.value;
    setFlashcards(newFlashcards);
  };

  const handleBackChange = (index, e) => {
    const newFlashcards = [...flashcards];
    newFlashcards[index].back = e.target.value;
    setFlashcards(newFlashcards);
  };

  // Then modify your handleAddCard function
  const handleAddCard = (index = -1) => {
    const newFlashcards = [...flashcards];
    newFlashcards.splice(index + 1, 0, {
      front: "",
      back: "",
      correct: "false",
      imageUrlFront: "",
      imageUrlBack: "",
    });
    setFlashcards(newFlashcards);
    setFocusIndex(index + 1); // Set the index to focus after render
  };
  // const handleAddCard = (index = -1) => {
  //   const newFlashcards = [...flashcards];
  //   newFlashcards.splice(index + 1, 0, {
  //     front: "",
  //     back: "",
  //     correct: "false",
  //     imageUrlFront: "",
  //     imageUrlBack: "",
  //   });
  //   setFlashcards(newFlashcards);
  //   setTimeout(() => {
  //     document.getElementById(`front-${index + 1}`).focus();
  //   }, 100);
  // };

  const handleDeleteCard = (index) => {
    const newFlashcards = flashcards.filter((_, i) => i !== index);
    setFlashcards(newFlashcards);
  };

  const handleTabPress = (index, isBack, e) => {
    if (e.key === "Tab" && !e.shiftKey) {
      e.preventDefault();
      if (isBack) {
        if (index === flashcards.length - 1) {
          handleAddCard(index);
        } else {
          document.getElementById(`front-${index + 1}`).focus();
        }
      } else {
        document.getElementById(`back-${index}`).focus();
      }
    }
  };

  const handleSubmit = async () => {
    if (!collectionName.trim()) {
      showSnackbar("Samlingens namn får inte vara tomt.", "info");
      collectionNameRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return;
    }
    if (
      flashcards.some((card) => !card?.front?.trim() || !card?.back?.trim())
    ) {
      showSnackbar(
        "Inga fält för framsida eller baksida får vara tomma.",
        "info",
      );
      return;
    }

    setLoadingSaving(true);
    setMessage("");
    let userId = user.uid;
    Logger.log("userId flashcard create: ", userId);

    try {
      // Sanitize the collection name
      const sanitizedCollectionName = sanitizeInput(collectionName);

      // Sanitize the flashcards
      const sanitizedFlashcards = flashcards.map((card) => ({
        front: sanitizeInput(card.front),
        back: sanitizeInput(card.back),
        correct: card.correct,
        imageUrlFront: card.imageUrlFront || "",
        imageUrlBack: card.imageUrlBack || "",
      }));

      // Sanitize other string fields
      const sanitizedCategory = sanitizeInput(category);
      const sanitizedLanguage = sanitizeInput(language);
      const sanitizedDifficulty = sanitizeInput(difficulty);
      const sanitizedAiInstructions = sanitizeInput(aiInstructions);
      const sanitizedSelectedModel = sanitizeInput(selectedModel);

      const collectionRef = await addDoc(
        collection(db, "flashcardCollections"),
        {
          userId,
          collectionName: sanitizedCollectionName,
          flashcards: sanitizedFlashcards,
          isPublic,
          createdAt: Date.now(),
          numFlashcards,
          category: sanitizedCategory,
          language: sanitizedLanguage,
          difficulty: sanitizedDifficulty,
          aiInstructions: sanitizedAiInstructions,
          selectedModel: sanitizedSelectedModel,
          lastTrainingSession: Date.now(),
          upvotes: 0,
          downvotes: 0,
          userVotes: {},
        },
      );

      const newCollection = {
        id: collectionRef.id,
        userId,
        collectionName: sanitizedCollectionName,
        flashcards: sanitizedFlashcards,
        isPublic,
      };

      onCollectionCreated(newCollection);
      showSnackbar(`Samlingen har skapats framgångsrikt!`, "success");
      setCollectionName("");
      setFlashcards([
        {
          front: "",
          back: "",
          correct: "false",
          imageUrlFront: "",
          imageUrlBack: "",
        },
      ]);

      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }, 50);
    } catch (error) {
      showSnackbar("Fel vid skapande av samling: " + error.message, "info");
    } finally {
      setLoadingSaving(false);
    }
  };

  const simulateProgress = (startProgress, endProgress, duration) => {
    const stepSize = 0.5;
    const steps = (endProgress - startProgress) / stepSize;
    const stepDuration = duration / steps;
    let currentProgress = startProgress;

    const intervalId = setInterval(() => {
      currentProgress += stepSize;
      if (currentProgress >= endProgress) {
        clearInterval(intervalId);
      } else {
        setProgress(currentProgress);
      }
    }, stepDuration);

    return () => clearInterval(intervalId);
  };

  const handleGenerateFlashcards = async () => {
    setProgress(0);
    setLoading(true);
    setMessage("");
    setFlashcards([]);

    Logger.log("inputType ", inputType);
    Logger.log("aiInput ", aiInput);
    Logger.log("lessonTheme: ", lessonTheme);
    Logger.log("Generating flashcards with:", {
      numFlashcards,
      category,
      language,
      difficulty,
      aiInstructions,
    });

    let userInput;

    if (lessonTheme === "youtube") {
      userInput = transcriptRef.current;
    } else if (lessonTheme === "customTheme") {
      userInput = customTheme;
    } else if (lessonTheme === "predefined") {
      userInput = predefinedTheme;
    } else if (lessonTheme === "fileUpload") {
      userInput = uploadedFileRef.current;
    } else if (lessonTheme === "wikipedia") {
      userInput = wikipediaContentRef.current;
    } else if (lessonTheme === "randomizer") {
      userInput = randomizedContentRef.current;
    } else if (lessonTheme === "book") {
      userInput = bookContentRef.current;
    } else if (lessonTheme === "url") {
      userInput = urlContentRef.current;
    } else if (lessonTheme === "vision") {
      userInput = analysisContentRef.current;
    } else if (lessonTheme === "pasteText") {
      userInput = pasteTextContentRef.current;
    } else {
      //
      userInput = coursePlanContentRef.current;
    }

    Logger.log("themeToUse: ", userInput);

    if (!collectionName.trim()) {
      showSnackbar("Fyll i namn på samlingen.", "info");
      setLoading(false);
      return;
    }

    if (!userInput?.trim()) {
      showSnackbar(
        "Du måste välja underlag för dina flashcards i steg 1.",
        "info",
      );
      setLoading(false);
      return;
    }

    let stopProgressSimulation = null;
    try {
      Logger.log("Selected model:", selectedModel);
      stopProgressSimulation = simulateProgress(0, 90, 15000);
      let diff =
        difficulty === "Svårt"
          ? "Extremt svåra flashcards, ytterst sällsynta."
          : difficulty;

      const messages = [
        {
          role: "system",
          content: `Generate EXACTLY ${numFlashcards} flashcards with the following format with "front" and "back" fields based on the users input.

---

Om det handlar om Matematik: När du använder LaTeX-syntax MÅSTE du omsluta VARJE matematiskt uttryck (oavsett om det är enkla operationer eller komplexa ekvationer) med dollartecken på följande sätt:

För uttryck inom textrader: $x \times y$
För fristående uttryck: $$x \times y$$

Använd ALDRIG [], () eller andra tecken runt matematiska uttryck. ALLTID dollartecken.

----


           Everything should be in ${language}. Difficulty level: ${difficulty}. ${aiInstructions}. Follow this exact format every time: [{ "front": "question", "back": "answer" }, { "front": "question2", "back": "answer2" }].`,
        },
        { role: "user", content: userInput },
      ];
      const response = await callApi(messages, undefined, true, user.uid);

      Logger.log("response: ", response);

      let messageContent = response.data.choices[0].message.content.trim();
      Logger.log("messageContent", messageContent);
      if (messageContent.startsWith("```json")) {
        messageContent = messageContent.slice(7, -3).trim();
      } else if (messageContent.startsWith("```")) {
        messageContent = messageContent.slice(3, -3).trim();
      }
      messageContent = escapeLatexInJson(messageContent);

      // Kontrollera om JSON-strukturen är korrekt
      const parsedFlashcards = JSON.parse(messageContent);
      Logger.log("parsedFlashcards", parsedFlashcards);

      if (!Array.isArray(parsedFlashcards)) {
        throw new Error(
          "The response format is incorrect. Expected an array of flashcards.",
        );
      }

      if (stopProgressSimulation) {
        stopProgressSimulation();
      }

      setProgress(100);

      // Se till att varje objekt har rätt nycklar

      const formattedFlashcards = parsedFlashcards.map((card) => {
        if (card.front && card.back) {
          return {
            front: card.front,
            back: card.back,
            correct: "false",
          };
        } else {
          throw new Error(
            'Flashcard format is incorrect. Each card must have "front" and "back" fields.',
          );
        }
      });

      //setFlashcards(formattedFlashcards);

      let index = 0;
      for (let i = 0; i < formattedFlashcards.length; i++) {
        await new Promise((resolve) => setTimeout(resolve, 100)); // 100ms fördröjning
        setFlashcards((prev) => [...prev, formattedFlashcards[i]]);
      }

      //setRenderInterval(interval);

      await trackLesson(user.uid, "Flashcards.js", null, {
        lessonTheme: lessonTheme,
      });

      setAIInput("");

      selectedSuggestionRef.current = "";
    } catch (error) {
      console.error(error);
      showSnackbar(
        "Fel vid generering av flashcards. Vänligen klicka på Generera igen. ",
        "info",
      );
      setProgress(0);
    } finally {
      setLoading(false);
      setProgress(0);
    }
  };

  return (
    <>
      <Box sx={styles.container}>
        <Box
          sx={{ ...styles.innerContainer, width: "100%", paddingRight: "0px" }}
        >
          {error && (
            <Snackbar
              open={!!error}
              onClose={() => setError(null)}
              anchorOrigin={{ vertical: "top", horizontal: "center" }}
              autoHideDuration={4000} // Optional: Automatically hides the alert after 6 seconds
            >
              <Alert
                onClose={() => setError(null)}
                severity="info"
                sx={{ width: "100%" }} // Ensures the Alert takes full width of the Snackbar
              >
                {` ${error}`}
              </Alert>
            </Snackbar>
          )}

          <Box sx={{ width: { xs: "100%", md: "100%" }, margin: "0 auto" }}>
            <Typography
              variant="h1"
              sx={{
                ...styles.newH1,

                width: { xs: "100%", md: "30%" },
              }}
            >
              Flashcards
            </Typography>
            {currentStep !== 3 && (
              <InfoBox
                icon={ViewAgendaIcon}
                title="Flashcards"
                instructions={[
                  "Välj underlag till dina flashcards och generera — eller skapa manuellt",
                  "Starta igång träningen direkt eller dela flashcards med elever",
                  "Se din historik över resultat, senaste träningstillfället och mycket mer",
                ]}
                theme={theme}
                styles={styles}
                user={user}
                flashcards={true}
              />
            )}

            {currentStep === 3 && (
              <>
                {user.accountType === "teacher" && (
                  <Typography
                    variant="subtitle1"
                    sx={{ color: "text.secondary", textAlign: "center" }}
                  >
                    Här kan du lägga till, redigera, spara, ta bort och dela
                    dina flashcards.
                  </Typography>
                )}

                {user.accountType === "student" && (
                  <>
                    <Typography
                      variant="subtitle1"
                      sx={{ color: "text.secondary", textAlign: "center" }}
                    >
                      Här kan du lägga till, redigera, spara eller ta bort
                      flashcards. <br />
                      Klicka på <strong>Börja öva</strong> när du är klar.
                    </Typography>
                    {isMobile && (
                      <Box sx={{ textAlign: "center", mt: 3 }}>
                        <Button
                          variant="purp"
                          color="primary"
                          onClick={handleSubmit}
                          disabled={loadingSaving}
                          startIcon={
                            !loadingSaving ? (
                              <PlayArrowIcon />
                            ) : (
                              <CircularProgress size={18} />
                            )
                          }
                        >
                          Börja öva
                        </Button>
                      </Box>
                    )}
                  </>
                )}
              </>
            )}

            {currentStep === 1 && (
              <ThemeSelectorFlow
                value={lessonTheme}
                onChange={(e) => setLessonTheme(e.target.value)}
                customTheme={customTheme}
                onCustomThemeChange={(e) => {
                  Logger.log("onCustomThemeChange", e.target.value);
                  setCustomTheme(e.target.value);
                  setCurrentStep(2);
                }}
                onPastedTextChange={(content) => {
                  Logger.log("onPastedTextChange", content.target.value);
                  pasteTextContentRef.current = content.target.value;
                  setCurrentStep(2);
                }}
                onAnalysisContentChange={(content) => {
                  Logger.log("onAnalysisContentChange", content.analysis);
                  analysisContentRef.current = content.analysis;
                  setCurrentStep(2);
                }}
                onBookContentChange={(content) => {
                  Logger.log("onBookContentChange", content.substring(0, 100));
                  bookContentRef.current = content;
                  setCurrentStep(2);
                }}
                onUrlContentChange={(content) => {
                  Logger.log(
                    "Parent: URL content received",
                    content.substring(0, 300),
                  );
                  urlContentRef.current = content;
                  setCurrentStep(2);
                }}
                onPredefinedThemeChange={(e) =>
                  setPredefinedTheme(e.target.value)
                }
                onYoutubeTranscriptChange={(transcriptContent) => {
                  Logger.log(
                    "onYoutubeTranscriptChange körs med: ",
                    transcriptContent,
                  );
                  transcriptRef.current = transcriptContent;
                  setCurrentStep(2);
                }}
                onFileUpload={(content) => {
                  Logger.log("onFileUpload körs med: ", content);
                  uploadedFileRef.current = content;
                  setCurrentStep(2);
                }}
                onWikipediaContentChange={(content) => {
                  Logger.log(
                    "Parent: Wikipedia content received",
                    content.substring(0, 100),
                  );
                  wikipediaContentRef.current = content;
                  setCurrentStep(2);
                }}
                onRandomizedContentChange={(content) => {
                  Logger.log("randomizedContentRef", content.substring(0, 100));
                  randomizedContentRef.current = content;
                  setCurrentStep(2);
                }}
                onCoursePlanContentChange={(content) => {
                  Logger.log("onCoursePlanChange", content.substring(0, 100));
                  coursePlanContentRef.current = content;
                  setCurrentStep(2);
                }}
                lessonType="Flashcards"
                user={user}
                theme={theme}
                styles={styles}
                additionalThemes={additionalThemes}
                forFlashcards={true}
              />
            )}
          </Box>
          {currentStep === 2 && (
            <>
              <BackButton
                onClick={() => {
                  setCurrentStep(currentStep - 1);
                  setFlashcards([]);
                  setCollectionName("");
                  resetThemeContent();
                }}
                show={currentStep !== 1}
              />
              <AnimatedStep isRevealed={true}>
                <Box sx={{ ...styles.headlineLessonBox, mt: 6 }}>
                  <Typography variant="h6" sx={styles.headlineLessonBoxTitle}>
                    <span>2</span> Generera flashcards
                  </Typography>
                </Box>

                {showAIInput && (
                  <Box
                    sx={{
                      ...styles.freshBox,
                      position: "relative",
                      padding: "40px",
                    }}
                  >
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Typography
                          variant="formLabelSinUnderline"
                          sx={{ display: "block", fontWeight: "500" }}
                        >
                          Namn på din samling
                        </Typography>

                        <TextField
                          ref={collectionNameRef}
                          placeholder="Namn på samlingen"
                          value={collectionName}
                          onChange={handleCollectionNameChange}
                          margin="normal"
                          autoFocus
                          fullWidth
                          sx={{ mt: 1 }}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Typography
                          variant="formLabelSinUnderline"
                          sx={{ display: "block", fontWeight: "500" }}
                        >
                          Språk
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          <LanguageSelector
                            language={language}
                            setLanguage={setLanguage}
                          />
                        </Box>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                          <Typography
                            variant="formLabelSinUnderline"
                            sx={{ display: "block", fontWeight: "500" }}
                          >
                            Antal flashcards
                          </Typography>
                          <Select
                            labelId="num-flashcards-label"
                            id="num-flashcards-select"
                            value={numFlashcards}
                            sx={{ mt: 1 }}
                            onChange={(e) => setNumFlashcards(e.target.value)}
                          >
                            <MenuItem value={2}>2</MenuItem>
                            <MenuItem value={5}>5</MenuItem>
                            <MenuItem value={10}>10</MenuItem>

                            {user.isPremium ? (
                              [
                                <MenuItem key="15" value={15}>
                                  15
                                </MenuItem>,
                                <MenuItem key="20" value={20}>
                                  20
                                </MenuItem>,
                                <MenuItem key="25" value={25}>
                                  25
                                </MenuItem>,
                                <MenuItem key="30" value={30}>
                                  30
                                </MenuItem>,
                              ]
                            ) : (
                              <Tooltip title="Endast tillgängligt för premium-användare">
                                <span>
                                  <MenuItem value={15} disabled>
                                    15
                                  </MenuItem>
                                  <MenuItem value={20} disabled>
                                    20
                                  </MenuItem>
                                  <MenuItem value={25} disabled>
                                    25
                                  </MenuItem>
                                  <MenuItem value={30} disabled>
                                    30
                                  </MenuItem>
                                </span>
                              </Tooltip>
                            )}
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* Language */}
                      <Grid item xs={12} md={6}>
                        <Typography
                          variant="formLabelSinUnderline"
                          sx={{ fontWeight: "500" }}
                        >
                          AI-modell
                        </Typography>

                        <AIModelInfoTooltip>
                          <span style={{ cursor: "help" }}>
                            <QuestionMarkIcon
                              sx={{
                                fontSize: 16,
                                ml: 1,
                                verticalAlign: "middle",
                              }}
                            />
                          </span>
                        </AIModelInfoTooltip>

                        <AIModelSelect
                          //disabledOptions={["Gemini Pro", "Mistral Large"]}
                          selectedModel={selectedModel}
                          setSelectedModel={setSelectedModel}
                        />
                      </Grid>
                    </Grid>

                    <Collapse in={showMoreOptions}>
                      <div style={{ marginTop: "0px" }}>
                        <AdditionalFlashFields
                          aiInstructions={aiInstructions}
                          setAiInstructions={setAiInstructions}
                          numFlashcards={numFlashcards}
                          setNumFlashcards={setNumFlashcards}
                          category={category}
                          setCategory={setCategory}
                          language={language}
                          setLanguage={setLanguage}
                          difficulty={difficulty}
                          setDifficulty={setDifficulty}
                          visibility={visibility}
                          handleVisibilityChange={handlePublicChange}
                          theme={theme}
                          selectedModel={selectedModel}
                          setSelectedModel={setSelectedModel}
                          user={user}
                        />
                      </div>
                    </Collapse>

                    <Stack
                      direction="column"
                      spacing={2}
                      sx={{
                        mt: 7,
                        justifyContent: "center",
                        alignItems: { xs: "stretch", md: "center" },
                        position: "relative",
                        width: "100%",
                      }}
                      className="progress-bar-container"
                    >
                      <Button
                        onClick={handleGenerateFlashcards}
                        variant={
                          theme.palette.mode === "light" ? "purp" : "contained"
                        }
                        color="primary"
                        sx={{ width: { xs: "100%", md: "250px" } }}
                        disabled={loading}
                        startIcon={
                          !loading ? (
                            <GenerateIcon style={{ color: "#fff" }} />
                          ) : (
                            <CircularProgress
                              style={{ color: "#fff" }}
                              size={18}
                            />
                          )
                        }
                      >
                        Generera flashcards
                      </Button>

                      {user.accountType === "teacher" && (
                        <>
                          <Typography
                            sx={{
                              mx: { xs: 0, md: 4 },
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              position: "relative",
                              fontWeight: "500",

                              fontSize: "0.9rem",
                              py: { xs: 2, md: 2 },
                              "&::before, &::after": {
                                content: '""',
                                position: "absolute",
                                top: "50%",
                                width: { xs: "30%", md: "40%" },
                                height: "1px",
                                backgroundColor: (theme) =>
                                  theme.palette.mode === "dark"
                                    ? "grey.800"
                                    : "grey.500",
                              },
                              "&::before": { left: { xs: "0", md: "43px" } },
                              "&::after": { right: { xs: "0", md: "43px" } },
                            }}
                          >
                            ELLER
                          </Typography>
                          <Button
                            startIcon={<AddIcon />}
                            variant="print"
                            onClick={() => {
                              if (!collectionName.trim()) {
                                showSnackbar(
                                  "Samlingens namn får inte vara tomt.",
                                  "info",
                                );
                              } else {
                                handleAddCard(flashcards.length - 1);
                              }
                            }}
                            sx={{ width: { xs: "100%", md: "250px" } }}
                          >
                            Skapa manuellt
                          </Button>
                        </>
                      )}
                      <Button
                        variant="print"
                        disableRipple
                        startIcon={
                          showMoreOptions ? (
                            <ExpandLessIcon />
                          ) : (
                            <SettingsIcon />
                          )
                        }
                        onClick={() => setShowMoreOptions(!showMoreOptions)}
                        sx={{
                          position: { xs: "relative", md: "absolute" },
                          right: { xs: "auto", md: "0px" },
                          bottom: { xs: "auto", md: "0px" },
                          marginRight: "0px",
                          paddingLeft: "20px",
                          paddingRight: "8px",
                          paddingTop: "14px",
                          paddingBottom: "14px",
                          minWidth: "auto",
                          textTransform: "none",
                          alignSelf: { xs: "center", md: "auto" },
                          mt: { xs: 2, md: 0 },
                        }}
                      />
                    </Stack>

                    {loading && (
                      <>
                        <Box sx={{ mt: 6, display: "none" }}>
                          <ProgressBar
                            progress={progress}
                            label="Genererar flashcards — tar 30-60 sekunder..."
                          />
                        </Box>
                      </>
                    )}
                  </Box>
                )}
              </AnimatedStep>
            </>
          )}
          {flashcards.length === 0 && !loading && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleAddCard()}
              sx={{ mt: 2, mb: 2, width: "170px", display: "none" }}
              startIcon={<AddIcon />}
            >
              Lägg till kort
            </Button>
          )}

          {currentStep === 3 && (
            <>
              <BackButton
                onClick={() => {
                  setCurrentStep(1);
                  setFlashcards([]);
                  setCollectionName("");
                  resetThemeContent();
                }}
                show={currentStep !== 1}
              />

              <Box sx={{ ...styles.headlineLessonBox, mt: 0, display: "none" }}>
                <Typography
                  variant="h6"
                  sx={{ ...styles.headlineLessonBoxTitle, display: "none" }}
                >
                  <span>3</span> Dina flashcards
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  gap: 3,
                  flexDirection: {
                    xs: "column",
                    md: "row",
                  },
                }}
              >
                <ShareDialog
                  open={shareDialogOpen}
                  onClose={() => setShareDialogOpen(false)}
                  onShare={handleShare}
                  user={user}
                />

                <ShareDialog
                  open={shareDialogOpenFriends}
                  onClose={() => setShareDialogOpenFriends(false)}
                  onShare={handleShare}
                  user={user}
                  theme={theme}
                  shareWithFriends={true}
                />

                <Box sx={{ flex: "1 1 auto", paddingRight: 1.5 }}>
                  {flashcards.map((flashcard, index) => (
                    <FlashcardItem
                      flashcard={flashcard}
                      index={index}
                      handleFrontChange={handleFrontChange}
                      handleBackChange={handleBackChange}
                      handleDeleteCard={handleDeleteCard}
                      handleTabPress={handleTabPress}
                      isDark={isDark}
                      styles={styles}
                      user={user}
                      showSnackbar={showSnackbar}
                      onImageUpdate={(index, url, side) => {
                        const newFlashcards = [...flashcards];
                        if (side === "front") {
                          newFlashcards[index].imageUrlFront = url;
                        } else {
                          newFlashcards[index].imageUrlBack = url;
                        }
                        setFlashcards(newFlashcards);
                      }}
                    />
                  ))}
                  {flashcards.length > 0 && (
                    <Grid container spacing={4} sx={{ width: "100%", mt: 2 }}>
                      <Grid item xs={12} md={6}>
                        <DashedPaper
                          onClick={() => {
                            // Add AI flashcard generation logic here
                            handleOpenAiFlashcardDialog();
                          }}
                          sx={{ minHeight: 200 }}
                        >
                          <Box sx={{ textAlign: "center" }}>
                            <AutoFixHighIcon
                              sx={{
                                fontSize: 48,
                                color: theme.palette.primary.main,
                                mb: 2,
                              }}
                            />
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              Lägg till flashcard med AI
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mt: 1 }}
                            >
                              Låt AI skapa ett nytt flashcard
                            </Typography>
                          </Box>
                        </DashedPaper>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <DashedPaper
                          onClick={() => handleAddCard(flashcards.length - 1)}
                          sx={{ minHeight: 200 }}
                        >
                          <Box sx={{ textAlign: "center" }}>
                            <AddIcon
                              sx={{
                                fontSize: 48,
                                color: theme.palette.primary.main,
                                mb: 2,
                              }}
                            />
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              Lägg till flashcard manuellt
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mt: 1 }}
                            >
                              Skriv fram- och baksida själv
                            </Typography>
                          </Box>
                        </DashedPaper>
                      </Grid>
                    </Grid>
                  )}
                </Box>

                <Box sx={{ position: "relative" }}>
                  <Box
                    sx={{
                      ...styles.lessonControlPanelNuevo2,

                      "& .MuiButton-root": {
                        ...styles.lessonControlPanelNewButtons2,
                      },
                    }}
                  >
                    {user.accountType === "student" && (
                      <>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600, color: "text.secondary" }}
                        >
                          Öva
                        </Typography>

                        <Button
                          variant={
                            theme.palette.mode === "light"
                              ? "purp"
                              : "contained"
                          }
                          color="primary"
                          onClick={handleSubmit}
                          disabled={loadingSaving}
                          startIcon={
                            !loadingSaving ? (
                              <PlayArrowIcon />
                            ) : (
                              <CircularProgress size={18} />
                            )
                          }
                        >
                          Börja öva
                        </Button>
                      </>
                    )}
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        color: "text.secondary",
                        mt: user.accountType === "teacher" ? 0 : 3,
                      }}
                    >
                      Lägg till flashcards
                    </Typography>

                    <Button
                      variant="print"
                      startIcon={<AutoFixHighIcon />}
                      onClick={() => handleOpenAiFlashcardDialog()}
                    >
                      Låt AI skapa
                    </Button>
                    <Button
                      variant="print"
                      startIcon={<AddIcon />}
                      onClick={() => handleAddCard(flashcards.length - 1)}
                    >
                      Skapa manuellt
                    </Button>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, color: "text.secondary", mt: 3 }}
                    >
                      Spara
                    </Typography>

                    <Tooltip title="Sparas i 'Mina innehåll'">
                      <Button
                        variant="print"
                        onClick={handleSaveFlashcards}
                        startIcon={
                          !loadingSaving ? (
                            <SaveAsIcon />
                          ) : (
                            <CircularProgress
                              sx={{ color: "white" }}
                              size={18}
                            />
                          )
                        }
                      >
                        Spara
                      </Button>
                    </Tooltip>

                    {user.accountType === "teacher" && (
                      <>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 600,
                            color: "text.secondary",
                            mt: 3,
                          }}
                        >
                          Visa
                        </Typography>
                        <Tooltip title="Interaktivt läge" placement="top">
                          <Button
                            variant={
                              theme.palette.mode === "light"
                                ? "purp"
                                : "contained"
                            }
                            color="primary"
                            onClick={handleSubmit}
                            disabled={loadingSaving}
                            startIcon={
                              !loadingSaving ? (
                                <PlayArrowIcon />
                              ) : (
                                <CircularProgress size={18} />
                              )
                            }
                          >
                            Elevläge
                          </Button>
                        </Tooltip>
                      </>
                    )}

                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, color: "text.secondary", mt: 3 }}
                    >
                      Dela
                    </Typography>
                    {user.accountType === "teacher" && (
                      <Button
                        variant="print"
                        onClick={() => setShareDialogOpen(true)}
                        startIcon={<ShareIcon />}
                      >
                        Dela med elever
                      </Button>
                    )}

                    <Button
                      variant="print"
                      onClick={() => setShareDialogOpenFriends(true)}
                      startIcon={<ShareIcon />}
                    >
                      Dela med vänner
                    </Button>
                  </Box>
                </Box>
              </Box>
            </>
          )}
          {message && (
            <Typography variant="body1" color="textSecondary" sx={{ mt: 2 }}>
              {message}
            </Typography>
          )}
        </Box>
      </Box>

      <LoadingDialog
        open={loading}
        onClose={() => {}} // You can add a function here if needed
        progress={progress}
        progressLabel={"Genererar flashcards — tar 10-20 sekunder..."}
        title="Genererar flashcards. Snart klart..."
        snackbarMessage="Genereringen är snart klar. Vänligen vänta."
        snackbarSeverity="info"
        progressBarHeight="15px"
        progressBarFontSize="0.9rem"
        coffee={false}
      />

      <LoadingBackdrop
        isOpen={isSharing}
        imagePath="/img/AI.png"
        loadingText={"Delar flashcards med eleverna..."}
      />

      <Dialog
        open={aiFlashcardDialogOpen}
        onClose={handleCloseAiFlashcardDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          Skapa flashcard med AI
          <IconButton
            aria-label="close"
            onClick={handleCloseAiFlashcardDialog}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2, mt: 2 }}>
            Beskriv vad det nya flashcardet ska handla om:
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            placeholder="T.ex. 'Skapa ett flashcard om fotosyntes', 'Gör ett flashcard om Sveriges huvudstad', 'Skapa ett flashcard om addition'"
            value={aiFlashcardPrompt}
            onChange={(e) => setAiFlashcardPrompt(e.target.value)}
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleAddFlashcardWithAI}
            variant="purp"
            disabled={isGeneratingFlashcard}
            startIcon={
              isGeneratingFlashcard ? (
                <CircularProgress sx={{ color: "white" }} size={16} />
              ) : (
                <AutoFixHighIcon />
              )
            }
          >
            {isGeneratingFlashcard
              ? "Skapar flashcard..."
              : "Generera flashcard"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FlashcardsCreateView;
